/**
 * HATCH gradient ramp texture cache (slice 2).
 *
 * AutoCAD gradient hatches (LINEAR / CYLINDER / SPHERICAL / …) carry a colour
 * ramp resolved by the shared {@link import("./gradientRamp").resolveGradientRamp}
 * core. This module bakes that ramp into an RGBA `CanvasTexture` so the on-screen
 * renderer (`scene3d/scene/sceneMeshFactories.buildHatchFillMesh`) can sample it
 * across the fill via per-vertex UVs — the on-screen parity with the PDF
 * axial/radial `/Shading`. The layout branches on shading family:
 *   - AXIAL  → a 256×1 1-D ramp sampled by an affine axis-projection U;
 *   - RADIAL → a 256×256 2-D disc with the falloff baked into the texels and
 *     sampled by equal-world-scale centre-relative UV (half-diagonal on BOTH axes
 *     → circular falloff like the PDF, P2-A; a per-vertex radius U interpolates
 *     flat across earcut's boundary-only triangulation — P2-1).
 * Both are tagged `SRGBColorSpace` (the texels are colour data — P2-2).
 *
 * # Why split out of hatchPatterns.ts
 *
 * The gradient cache MIRRORS the pattern cache (LRU, jsdom 1×1 fallback, dispose
 * on eviction) but is a distinct texture family with its OWN wrapping
 * (ClampToEdge — the ramp is sampled once, not tiled), pixel layout (256×1 vs
 * 64×64) and key. Keeping it here keeps both files focused (hatchPatterns.ts
 * stayed within its size band) while `hatchPatterns.ts` BARREL RE-EXPORTS the
 * gradient API so existing import paths (`from "../hatch/hatchPatterns"`) are
 * unchanged — a re-export, NOT a moved import (dep-graph-refactor safe).
 */

import * as THREE from "three";

import { CURVED_EASING_N } from "./gradientRamp";

/**
 * Cache key for a baked gradient ramp. Captures everything that changes the
 * PIXELS of the 256×1 ramp — the two endpoint colours, the shading family, and
 * the symmetric / curved flags. It DELIBERATELY EXCLUDES `rotation` / `shift`:
 * those orient/slide the ramp via the mesh UVs (`buildHatchFillMesh`), NOT the
 * texels, so folding them into the key would thrash the LRU (every rotated copy
 * of one gradient would re-bake the same pixels). `inverse` is also excluded —
 * the INV* endpoint swap is already folded into `color0Hex` / `color1Hex` by
 * `resolveGradientRamp`, so two INV-swapped gradients with identical resolved
 * endpoints share one texture. `name` is carried for diagnostics but is NOT part
 * of the encoded id (the shadingType + flags fully determine the pixels).
 */
export interface GradientKey {
  name: string;
  shadingType: "axial" | "radial";
  color0Hex: string;
  color1Hex: string;
  symmetric: boolean;
  curved: boolean;
}

/** Encoded gradient cache key string. */
function gradientKeyId(k: GradientKey): string {
  return `${k.shadingType}|${k.color0Hex}|${k.color1Hex}|${k.symmetric ? "S" : "-"}|${k.curved ? "C" : "-"}`;
}

/** Width of the baked 1-D AXIAL ramp (1 texel tall — the V axis is unused). */
const GRADIENT_RAMP_WIDTH = 256;
/**
 * Side of the baked 2-D RADIAL texture. The radial falloff is baked into the
 * TEXELS (a `createRadialGradient` disc) and sampled with equal-world-scale
 * centre-relative UV (half-diagonal r1 on BOTH axes → circular falloff, P2-A), so
 * the falloff is affine-correct under ANY triangulation — earcut triangulates a
 * HATCH from boundary vertices only (no centre vertex), so a per-vertex
 * `radius/r1` U interpolates flat across a boundary-only fan and never reaches 0
 * at the centre (P2-1 root cause). Baking the disc into a 2-D texture moves the
 * falloff into the texels where interpolation can't flatten it.
 */
const RADIAL_TEX_SIZE = 256;
/**
 * Disc radius (in texels) for the baked radial gradient. The on-screen mesh UV
 * (sceneMeshFactories.buildGradientMaterial) uses EQUAL world-scale on both axes
 * — `UV = (0.5 + (x−cx)/(2·r1), 0.5 + (y−cy)/(2·r1))` with `r1 = hypot(w,h)/2`
 * (half the bbox diagonal, matching the PDF radialCoordsPt, batchPaint.ts:747-760)
 * — so the falloff is CIRCULAR in world space (P2-A fix), not stretched into an
 * ellipse on a non-square bbox. Under that mapping a vertex at world-distance r1
 * from the centre (the bbox CORNER) lands at UV-radius exactly 0.5 from
 * (0.5, 0.5). The disc must therefore reach color1 at UV-radius 0.5 → disc radius
 * = 0.5·texSize. Distances beyond it (vertices past the corner on a non-square
 * bbox, UV outside [0,1]) clamp (ClampToEdge) to color1, matching the axial
 * ClampToEdge end behaviour and the PDF `/Extend [true true]`.
 */
const RADIAL_DISC_RADIUS = 0.5 * RADIAL_TEX_SIZE;
/** Per-axial-texture bytes: 256×1×4 = 1 kB. */
const BYTES_PER_AXIAL = GRADIENT_RAMP_WIDTH * 1 * 4;
/** Per-radial-texture bytes: 256×256×4 = 256 kB. */
const BYTES_PER_RADIAL = RADIAL_TEX_SIZE * RADIAL_TEX_SIZE * 4;
/** Bytes a baked texture of the given shading family occupies. */
function bytesForShading(shadingType: GradientKey["shadingType"]): number {
  return shadingType === "radial" ? BYTES_PER_RADIAL : BYTES_PER_AXIAL;
}
/**
 * Max distinct gradient textures held. A drawing carries far fewer distinct
 * gradients than this, so the cap is effectively never hit — it exists only as a
 * defensive bound (mirrors the pattern cache's ≈128-entry budget).
 */
const MAX_GRADIENT_ENTRIES = 128;

/** Number of stops baked for a CURVED ramp (≥17 → a smooth eased curve). */
const CURVED_STOP_COUNT = 17;

/** Gradient LRU cache entry. */
interface CachedGradient {
  key: GradientKey;
  texture: THREE.CanvasTexture;
  touch: number;
}

const gradientCache: Map<string, CachedGradient> = new Map();
let gradientTouchCounter = 0;
let gradientCacheBytes = 0;

/** Linear interpolation of two channel values in 0..255. */
function lerpChannel(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Parse `#rrggbb` into an [r, g, b] triple (0..255). Black on a bad string. */
function hexToRgb255(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [0, 0, 0];
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

/**
 * Apply the gradient stops to a `CanvasGradient` per the gradient flags. SHARED
 * by the axial 1-D ramp (`createLinearGradient`) and the radial 2-D disc
 * (`createRadialGradient`) so BOTH families use the identical symmetric / curved
 * stop semantics:
 *   - symmetric → 3 stops 0 / 0.5 / 1 = c0 / c1 / c0 (CYLINDER — highlight in
 *     the middle);
 *   - curved    → {@link CURVED_STOP_COUNT} stops `lerp(c0, c1, t^N)` (eased);
 *   - else      → a plain 2-stop c0 → c1 ramp.
 *
 * For a radial disc the stop offset `t` runs centre (0) → edge (1), so symmetric
 * paints centre = c0, mid-radius = c1, edge = c0 and a plain ramp paints
 * centre = c0 → edge = c1 — the radial analogue of the axial start→end ordering.
 */
function applyGradientStops(grad: CanvasGradient, key: GradientKey): void {
  const [r0, g0, b0] = hexToRgb255(key.color0Hex);
  const [r1, g1, b1] = hexToRgb255(key.color1Hex);
  const rgba = (r: number, g: number, b: number) =>
    `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},1)`;

  if (key.symmetric) {
    grad.addColorStop(0, rgba(r0, g0, b0));
    grad.addColorStop(0.5, rgba(r1, g1, b1));
    grad.addColorStop(1, rgba(r0, g0, b0));
  } else if (key.curved) {
    for (let i = 0; i < CURVED_STOP_COUNT; i++) {
      const t = i / (CURVED_STOP_COUNT - 1);
      const eased = Math.pow(t, CURVED_EASING_N);
      grad.addColorStop(
        t,
        rgba(
          lerpChannel(r0, r1, eased),
          lerpChannel(g0, g1, eased),
          lerpChannel(b0, b1, eased),
        ),
      );
    }
  } else {
    grad.addColorStop(0, rgba(r0, g0, b0));
    grad.addColorStop(1, rgba(r1, g1, b1));
  }
}

/** Paint the 256×1 AXIAL ramp into a 2D context (left→right c0→c1). */
function paintGradientRamp(
  ctx: CanvasRenderingContext2D,
  key: GradientKey,
): void {
  const grad = ctx.createLinearGradient(0, 0, GRADIENT_RAMP_WIDTH, 0);
  applyGradientStops(grad, key);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, GRADIENT_RAMP_WIDTH, 1);
}

/**
 * Paint the 256×256 RADIAL disc into a 2D context. The falloff is baked into the
 * texels: a `createRadialGradient(cx, cy, 0, cx, cy, R)` centred at the texture
 * midpoint with `R = ` {@link RADIAL_DISC_RADIUS} = 0.5·texSize so the bbox corner
 * — which the equal-world-scale mesh UV places at UV-radius 0.5 from the centre
 * (sceneMeshFactories.buildGradientMaterial, P2-A) — maps to color1 (the disc
 * edge). `ClampToEdge` then carries color1 to every texel beyond the disc (UV
 * outside [0,1]², i.e. vertices past the corner on a non-square bbox). Uses the
 * SAME {@link applyGradientStops} semantics as the axial ramp so symmetric /
 * curved match.
 */
function paintRadialGradient(
  ctx: CanvasRenderingContext2D,
  key: GradientKey,
): void {
  const cx = RADIAL_TEX_SIZE / 2;
  const cy = RADIAL_TEX_SIZE / 2;
  // Fill the whole texture with the edge colour first so corners beyond the
  // disc read color1 (the ramp end) — createRadialGradient leaves the area
  // outside its outer circle painted with the LAST stop, but filling explicitly
  // keeps the behaviour deterministic across canvas backends.
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, RADIAL_DISC_RADIUS);
  applyGradientStops(grad, key);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, RADIAL_TEX_SIZE, RADIAL_TEX_SIZE);
}

/**
 * Look up (or bake) the `CanvasTexture` for a gradient ramp. MIRRORS
 * `getOrCreatePatternTexture` (LRU cache, jsdom 1×1 fallback, dispose on
 * eviction) but with `ClampToEdge` wrapping — the ramp is sampled ONCE across the
 * fill via per-vertex UVs, so the ends must clamp (a value > 1 or < 0 reads the
 * end colour, not a wrapped repeat).
 *
 * The texture LAYOUT branches on `key.shadingType` (the key already encodes it,
 * so axial and radial never collide):
 *   - AXIAL  → a 256×1 1-D ramp. The on-screen mesh projects each vertex onto
 *     the rotation axis to get its U; affine projection interpolates correctly
 *     across triangles, so a 1-D ramp is sufficient.
 *   - RADIAL → a 256×256 2-D disc with the falloff baked into the TEXELS
 *     (`createRadialGradient`). The mesh uses equal-world-scale centre-relative UV
 *     (half-diagonal r1 on BOTH axes → circular falloff like the PDF, P2-A);
 *     baking the falloff into texels keeps it affine-correct under the boundary-
 *     only earcut triangulation (a per-vertex `radius/r1` U would interpolate flat
 *     across a boundary fan and never reach 0 at the centre — P2-1 root cause).
 *
 * `colorSpace = SRGBColorSpace`: a `CanvasTexture` defaults to `NoColorSpace`,
 * but the baked ramp is COLOUR data, so under three.js's colour-managed pipeline
 * the intermediate ramp colours would be gamma-wrong vs the PDF `/Shading` ramp
 * without the sRGB tag (P2-2).
 *
 * Returns `null` only when even the jsdom fallback cannot allocate a canvas
 * (no `document` AND no `OffscreenCanvas`) — caller falls back to a flat fill.
 */
export function getOrCreateGradientTexture(
  key: GradientKey,
): THREE.CanvasTexture | null {
  const id = gradientKeyId(key);
  const cached = gradientCache.get(id);
  if (cached) {
    cached.touch = ++gradientTouchCounter;
    return cached.texture;
  }

  const isRadial = key.shadingType === "radial";

  const canvas =
    typeof document !== "undefined"
      ? document.createElement("canvas")
      : (null as unknown as HTMLCanvasElement);
  if (!canvas) {
    // jsdom / SSR fallback — synthesise a 1×1 ClampToEdge texture (mirrors the
    // pattern path). NOT cached: the real GPU bake replaces it on first browser
    // render. Exercised by unit tests under jsdom.
    const blankCanvas =
      typeof OffscreenCanvas !== "undefined"
        ? (new OffscreenCanvas(1, 1) as unknown as HTMLCanvasElement)
        : ({ width: 1, height: 1 } as unknown as HTMLCanvasElement);
    const texture = new THREE.CanvasTexture(blankCanvas);
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    // Colour data — tag sRGB so the fallback matches the real bake (harmless on
    // a 1×1 blank, but keeps the contract uniform).
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }
  if (isRadial) {
    canvas.width = RADIAL_TEX_SIZE;
    canvas.height = RADIAL_TEX_SIZE;
  } else {
    canvas.width = GRADIENT_RAMP_WIDTH;
    canvas.height = 1;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  if (isRadial) {
    paintRadialGradient(ctx, key);
  } else {
    paintGradientRamp(ctx, key);
  }

  const texture = new THREE.CanvasTexture(canvas);
  // Baked ramp is COLOUR data — tag sRGB FIRST (right after construction) so
  // three.js's colour-managed pipeline does NOT treat the texels as already-
  // linear, which would make intermediate ramp colours gamma-wrong vs the PDF
  // ramp (P2-2). Set before the other texture config for unambiguity.
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;

  const bytes = bytesForShading(key.shadingType);

  // LRU eviction (own counter / byte tally — separate from the pattern cache).
  if (gradientCache.size >= MAX_GRADIENT_ENTRIES) {
    let oldestKey: string | null = null;
    let oldestTouch = Infinity;
    for (const [k, e] of gradientCache) {
      if (e.touch < oldestTouch) {
        oldestTouch = e.touch;
        oldestKey = k;
      }
    }
    if (oldestKey !== null) {
      const stale = gradientCache.get(oldestKey);
      stale?.texture.dispose();
      gradientCache.delete(oldestKey);
      gradientCacheBytes -= bytesForShading(stale?.key.shadingType ?? "axial");
    }
  }

  gradientCache.set(id, { key, texture, touch: ++gradientTouchCounter });
  gradientCacheBytes += bytes;
  return texture;
}

/** Total bytes currently held by the gradient cache. */
export function getGradientCacheBytes(): number {
  return gradientCacheBytes;
}

/** Clear the gradient cache — hot reload + tests. Disposes GPU textures. */
export function clearGradientTextureCache(): void {
  for (const e of gradientCache.values()) {
    e.texture.dispose();
  }
  gradientCache.clear();
  gradientCacheBytes = 0;
  gradientTouchCounter = 0;
}
